import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userAPI } from '../services/api';

import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import ProfileImageUpload from '../components/ProfileImageUpload';
import { toast } from 'sonner';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import './Team.css';

const AddTeamMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  const isFromDashboard = location.state?.from === 'dashboard';
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    mobile: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    role: 'Developer',
    department: '',
    employeeId: '',
    joiningDate: new Date().toISOString().split('T')[0],
    employmentType: 'Full-time',
    workLocation: 'Office',
    designation: '',
    salary: '',
    reportsTo: '',
    education: '',
    university: '',
    graduationYear: '',
    experience: '',
    previousCompany: '',
    skills: '',
    profileImage: '',
    password: ''
  });
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');

  const roles = [
    'HR',
    'Operations Head',
    'Project Manager',
    'Project Lead',
    'Developer',
    'QA Engineer',
    'UI Engineer'
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!isValidPhoneNumber(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number for the selected country';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.joiningDate) {
      newErrors.joiningDate = 'Joining date is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const cleanValue = (value) => {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    return value;
  };

  const handleImageChange = async (file, preview) => {
    setProfileImageFile(file);
    if (file) {
      try {
        setUploadingImage(true);
        const response = await userAPI.uploadProfileImage(file);
        setFormData({ ...formData, profileImage: response.data.imageUrl });
      } catch (error) {
        console.error('Image upload error:', error);
        toast.error('Failed to upload image');
      } finally {
        setUploadingImage(false);
      }
    } else {
      setFormData({ ...formData, profileImage: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        username: formData.username.trim().toLowerCase(),
        email: formData.email.trim().toLowerCase(),
        mobile: formData.mobile.trim(),
        dateOfBirth: cleanValue(formData.dateOfBirth),
        gender: cleanValue(formData.gender),
        maritalStatus: cleanValue(formData.maritalStatus),
        nationality: cleanValue(formData.nationality),
        address: cleanValue(formData.address),
        city: cleanValue(formData.city),
        state: cleanValue(formData.state),
        zipCode: cleanValue(formData.zipCode),
        country: cleanValue(formData.country),
        emergencyContactName: cleanValue(formData.emergencyContactName),
        emergencyContactPhone: cleanValue(formData.emergencyContactPhone),
        emergencyContactRelation: cleanValue(formData.emergencyContactRelation),
        role: formData.role,
        department: cleanValue(formData.department),
        employeeId: cleanValue(formData.employeeId),
        joiningDate: formData.joiningDate,
        employmentType: cleanValue(formData.employmentType),
        workLocation: cleanValue(formData.workLocation),
        designation: cleanValue(formData.designation),
        salary: formData.salary ? Number(formData.salary) : undefined,
        reportsTo: formData.reportsTo || null,
        education: cleanValue(formData.education),
        university: cleanValue(formData.university),
        graduationYear: formData.graduationYear ? Number(formData.graduationYear) : undefined,
        experience: formData.experience ? Number(formData.experience) : undefined,
        previousCompany: cleanValue(formData.previousCompany),
        skills: cleanValue(formData.skills),
        profileImage: formData.profileImage || null,
        password: formData.password
      };

      // Remove undefined values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined) {
          delete submitData[key];
        }
      });

      console.log('Submitting user data:', submitData);
      await userAPI.create(submitData);
      toast.success('Team member added successfully!');
      navigate(isFromDashboard ? '/dashboard' : '/team');
    } catch (error) {
      console.error('Create user error:', error);
      console.error('Error response:', error.response);
      const errorMsg = error.response?.data?.message || 'Failed to add team member';
      if (errorMsg.includes('email')) {
        setErrors({ email: 'This email is already registered' });
      } else if (errorMsg.includes('username')) {
        setErrors({ username: 'This username is already registered' });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Add New Employee</h1>
            <p className="page-subtitle">Create a new team member profile</p>
          </div>
          {isFromDashboard ? (
            <button className="btn-back-arrow" onClick={() => navigate('/dashboard')}>
              ←
            </button>
          ) : (
            <button className="btn-back" onClick={() => navigate('/team')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back to Team
            </button>
          )}
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="team-form">
            <div className="form-section">
              <h3 className="section-title">Employee Information</h3>
              <div className="form-group">
                <label className="form-label">Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  disabled={submitting}
                  autoComplete="name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Username <span className="required">*</span></label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={`form-input ${errors.username ? 'error' : ''}`}
                    placeholder="e.g., john.smith"
                    disabled={submitting}
                    autoComplete="username"
                  />
                  {errors.username && <span className="error-message">{errors.username}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    disabled={submitting}
                    autoComplete="email"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Mobile Number *</label>
                  <div className={`premium-phone-input ${errors.mobile ? 'has-error' : ''}`}>
                    <div className="country-select-wrapper">
                      {selectedMobileCountry ? (
                        <>
                          <img
                            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                            alt={selectedMobileCountry}
                            className="flag-icon"
                          />
                          <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                        </>
                      ) : (
                        <div className="globe-icon">🌐</div>
                      )}
                      <select
                        className="country-native-select"
                        value={selectedMobileCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value || null;
                          setSelectedMobileCountry(newCountry);

                          // When country changes, if there's a number, try to re-validate it
                          // or if empty, set the prefix
                          if (newCountry) {
                            const callingCode = getCountryCallingCode(newCountry);
                            if (!formData.mobile || formData.mobile === '') {
                              // We don't necessarily want to force the prefix in the input if using national mode
                              // but we should clear any previous invalid state
                              setErrors(prev => ({ ...prev, mobile: '' }));
                            } else {
                              // Re-validate existing number with new country
                              // react-phone-number-input might handle the prefixing
                            }
                          }
                        }}
                        disabled={submitting}
                      >
                        <option value="">Select Country</option>
                        {getCountries().map((country) => (
                          <option key={country} value={country}>
                            {en[country]} (+{getCountryCallingCode(country)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="phone-input-field-wrapper">
                      <Input
                        country={selectedMobileCountry}
                        placeholder={selectedMobileCountry ? `Enter number for ${en[selectedMobileCountry]}` : "Select country first"}
                        value={formData.mobile}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, mobile: value || '' }));

                          // Real-time validation
                          if (value) {
                            if (isValidPhoneNumber(value)) {
                              setErrors(prev => ({ ...prev, mobile: '' }));
                            } else {
                              setErrors(prev => ({ ...prev, mobile: 'Please enter a valid mobile number for the selected country' }));
                            }
                          } else {
                            setErrors(prev => ({ ...prev, mobile: '' }));
                          }
                        }}
                        disabled={!selectedMobileCountry || submitting}
                        className="PhoneInputInput"
                      />
                    </div>
                  </div>
                  {errors.mobile && <span className="error-text">{errors.mobile}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={submitting}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Gender <span className="required">*</span></label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`form-select ${errors.gender ? 'error' : ''}`}
                    disabled={submitting}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Marital Status</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Nationality</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    placeholder="American"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Address Details</h3>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Zip/Postal Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="10001"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="United States"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Emergency Contact</h3>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                  placeholder="Jane Doe"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    placeholder="+1234567890"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Relationship</label>
                  <select
                    name="emergencyContactRelation"
                    value={formData.emergencyContactRelation}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Child">Child</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Employment Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role <span className="required">*</span></label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={`form-select ${errors.role ? 'error' : ''}`}
                    disabled={submitting}
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  {errors.role && <span className="error-message">{errors.role}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Department</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Quality Assurance">Quality Assurance</option>
                    <option value="Design">Design</option>
                    <option value="Product Management">Product Management</option>
                    <option value="Human Resources">Human Resources</option>
                    <option value="Operations">Operations</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Employee ID</label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    placeholder="EMP001"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    placeholder="Senior Developer"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Joining Date <span className="required">*</span></label>
                  <input
                    type="date"
                    name="joiningDate"
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                    className={`form-input ${errors.joiningDate ? 'error' : ''}`}
                    disabled={submitting}
                    max="9999-12-31"
                  />
                  {errors.joiningDate && <span className="error-message">{errors.joiningDate}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type</label>
                  <select
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                    <option value="Consultant">Consultant</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Work Location</label>
                  <select
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="Office">Office</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Annual Salary</label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    placeholder="50000"
                    className="form-input"
                    disabled={submitting}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Reports To</label>
                <select
                  name="reportsTo"
                  value={formData.reportsTo}
                  onChange={handleInputChange}
                  className="form-select"
                  disabled={submitting}
                >
                  <option value="">None</option>
                  {users.filter(u =>
                    u.role === 'Project Lead' ||
                    u.role === 'Project Manager' ||
                    u.role === 'Operations Head'
                  ).map(u => (
                    <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Education & Experience</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Highest Education</label>
                  <select
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Education</option>
                    <option value="High School">High School</option>
                    <option value="Associate Degree">Associate Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Doctorate">Doctorate</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">University/College</label>
                  <input
                    type="text"
                    name="university"
                    value={formData.university}
                    onChange={handleInputChange}
                    placeholder="MIT"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Graduation Year</label>
                  <input
                    type="number"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    placeholder="2020"
                    className="form-input"
                    disabled={submitting}
                    min="1950"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Years of Experience</label>
                  <input
                    type="number"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="5"
                    className="form-input"
                    disabled={submitting}
                    min="0"
                    max="50"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Previous Company</label>
                <input
                  type="text"
                  name="previousCompany"
                  value={formData.previousCompany}
                  onChange={handleInputChange}
                  placeholder="Google Inc."
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Skills (comma separated)</label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleInputChange}
                  placeholder="React, Node.js, MongoDB, AWS"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <ProfileImageUpload
                currentImage={formData.profileImage}
                onImageChange={handleImageChange}
                name={formData.name}
                disabled={submitting || uploadingImage}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">Security</h3>
              <div className="form-group">
                <label className="form-label">Initial Password <span className="required">*</span></label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Min 6 chars with uppercase, lowercase & number"
                    className={`form-input ${errors.password ? 'error' : ''}`}
                    disabled={submitting}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={submitting}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
                {!errors.password && <span className="field-hint">Employee can change password after first login</span>}
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate(isFromDashboard ? '/dashboard' : '/team')} disabled={submitting}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Add Employee'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddTeamMember;